import { DesignerPickerItemResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';

import { SubTicketReadState } from './subTicketListRead';

export interface DesignerPickerSource extends SubTicketReadState {
    items: DesignerPickerItemResource[] | null;
}

export interface DesignerPickerView {
    // Items to offer, or null whenever they are not this Request's items
    items: DesignerPickerItemResource[] | null;

    // The current selection, but ONLY when it is genuinely one of this Request's items
    selected: DesignerPickerItemResource | null;

    // The picker has answered for this Request and offers at least one designer
    canAssign: boolean;

    // The read for this Request is still in flight
    isLoading: boolean;

    // The read for this Request failed, so there is nothing to choose from and a retry is needed
    hasFailed: boolean;
}

// One gate between the global picker store and any modal that offers a designer.
//
// The picker names people assignable to ONE Request and carries their participation in it, so
// items held for a different Request are withheld outright rather than shown greyed out: seeding
// a modal from them would offer someone who may not be assignable here at all, and their
// `is_participant` flag would describe the wrong Request.
//
// The selection is re-derived from the offered items on every call rather than trusted. A
// selection made against Request A's picker cannot survive into Request B just because the
// component holding it did not unmount — it stops matching anything on offer, so it is dropped.
export const resolveDesignerPicker = (
    picker: DesignerPickerSource,
    taskId: ID,
    selected: DesignerPickerItemResource | null
): DesignerPickerView => {
    const isCurrentTask = picker.taskId === taskId;

    if (!isCurrentTask || picker.status !== 'loaded' || !picker.items) {
        return {
            items: null,
            selected: null,
            canAssign: false,
            isLoading: isCurrentTask ? picker.status === 'loading' : true,
            hasFailed: isCurrentTask && picker.status === 'error',
        };
    }

    const match =
        selected && selected.user
            ? picker.items.find((item) => item.user.id === selected.user.id) ?? null
            : null;

    return {
        items: picker.items,
        selected: match,
        canAssign: picker.items.length > 0,
        isLoading: false,
        hasFailed: false,
    };
};
